import React from 'react';
import { Link } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { TradingCard } from '../components/TradingCard';
import { ProfileSettings, SettingsIcon } from '../components/ProfileSettings';
import { ClompApi } from '../api/ClompApi';
import { endSession } from '../api/session';
import { Presence } from '../components/Presence';
import { Coins } from '../components/ClompCoin';
import './ProfilePage.css';

const api = new ClompApi();

// The games with scores, in the order their tabs are shown
const SCORE_GAMES = [
    { id: 'solarflare', name: 'Solarflare' },
    { id: 'spacejump', name: 'Space Jump' },
    { id: 'clompyclicker', name: 'Clompy Clicker' }
];

const RARITIES = [
    { id: 'common', label: 'Common' },
    { id: 'rare', label: 'Rare' },
    { id: 'epic', label: 'Epic' },
    { id: 'legendary', label: 'Legendary' }
];

// Seconds as minutes:seconds.tenths, the way Space Jump shows run times
function formatTime(seconds) {
    let tenths = Math.floor(seconds * 10);
    let wholeSeconds = Math.floor(tenths / 10) % 60;
    return `${Math.floor(tenths / 600)}:${wholeSeconds < 10 ? '0' : ''}${wholeSeconds}.${tenths % 10}`;
}

// The stand-in picture every player has until avatars can be chosen
export function DefaultAvatar() {
    return (
        <svg className="pf-avatar" viewBox="0 0 96 96" role="img" aria-label="Default avatar">
            <circle cx="48" cy="48" r="48" fill="#161616" />
            <circle cx="48" cy="38" r="17" fill="powderblue" />
            <path d="M17 82c3-17 15-27 31-27s28 10 31 27a45 45 0 0 1-62 0z" fill="powderblue" />
            <circle cx="48" cy="48" r="46.5" fill="none" stroke="powderblue" strokeWidth="3" />
        </svg>
    );
}

// One record with where it ranks among all players; the top three get a medal colour
function Record({ record, format }) {
    if (!record) {
        return <span className="pf-none">–</span>;
    }
    return (
        <>
            <span className="pf-record-value">{format(record.value)}</span>
            <span className={`pf-rank${record.rank <= 3 ? ` pf-rank-${record.rank}` : ''}`} title={`Ranked ${record.rank} of all players`}>#{record.rank}</span>
        </>
    );
}

export class ProfilePage extends React.Component {

    state = {
        loading: true,
        profile: null,
        // 'login' when /profile is opened logged out, 'missing' when there is no such player, 'error' otherwise
        problem: null,
        editingBio: false,
        bioDraft: '',
        savingBio: false,
        bioError: null,
        // the account settings form is open (your own profile only)
        showSettings: false,
        // which game's scores are showing
        scoreGame: SCORE_GAMES[0].id,
        // which game's achievements are showing (null for the first), and the games whose unearned achievements are
        // being shown too: { game: true }
        achievementGame: null,
        allAchievements: {}
    };

    componentDidMount() {
        this.load();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.match.params.id !== this.props.match.params.id) {
            this.load();
        }
    }

    load() {
        let id = this.props.match.params.id;
        this.setState({ loading: true, profile: null, problem: null, editingBio: false, showSettings: false, scoreGame: SCORE_GAMES[0].id, achievementGame: null, allAchievements: {} });
        if (id === undefined && localStorage.getItem('token') === null) {
            this.setState({ loading: false, problem: 'login' });
            return;
        }
        (id === undefined ? api.profile.getMine() : api.profile.get(id)).then((profile) => {
            this.setState({ loading: false, profile });
        }).catch((err) => {
            let status = err.response && err.response.status;
            if (status !== 401 && status !== 404) {
                console.log('Error loading profile: ', err);
            }
            this.setState({ loading: false, problem: status === 401 ? 'login' : status === 404 ? 'missing' : 'error' });
        });
    }

    saveBio = () => {
        this.setState({ savingBio: true, bioError: null });
        api.profile.setBio(this.state.bioDraft).then((result) => {
            this.setState({ profile: { ...this.state.profile, bio: result.bio }, editingBio: false, savingBio: false });
        }).catch((err) => {
            let message = err.response && err.response.data && err.response.data.message;
            this.setState({ savingBio: false, bioError: message || "Couldn't save your bio. Please try again." });
        });
    }

    // The account settings were saved: show the new name and bio, and move to the profile's new address if it was
    // opened by the old username
    settingsSaved = (settings) => {
        let renamed = settings.username !== this.state.profile.username;
        this.setState({ profile: { ...this.state.profile, username: settings.username, bio: settings.bio, presenceHidden: Boolean(settings.hidePresence) }, showSettings: false, editingBio: false });
        if (renamed && this.props.match.params.id !== undefined) {
            this.props.history.replace(`/profile/${encodeURIComponent(settings.username)}`);
        }
    }

    // The account is gone: forget its login and leave for the home page
    accountDeleted = () => {
        endSession(false);
        this.props.history.replace('/');
    }

    renderBio() {
        let { profile, editingBio, bioDraft, savingBio, bioError } = this.state;
        let max = profile.bioMaxLength || 500;
        return (
            <section className="pf-section">
                <div className="pf-section-head">
                    <h2>Biography</h2>
                    {profile.isMine && !editingBio ?
                        <button type="button" className="pf-btn pf-btn-ghost" onClick={() => this.setState({ editingBio: true, bioDraft: profile.bio, bioError: null })}>
                            {profile.bio ? 'Edit' : 'Add a bio'}
                        </button>
                        : null}
                </div>
                {editingBio ?
                    <div className="pf-bio-edit">
                        <textarea className="pf-bio-input" aria-label="Biography" rows="5" maxLength={max} value={bioDraft} autoFocus
                            placeholder="Tell other players a bit about yourself." onChange={(e) => this.setState({ bioDraft: e.target.value })} />
                        <div className="pf-bio-actions">
                            <span className="pf-bio-count">{bioDraft.length} / {max}</span>
                            <button type="button" className="pf-btn pf-btn-ghost" disabled={savingBio} onClick={() => this.setState({ editingBio: false })}>Cancel</button>
                            <button type="button" className="pf-btn pf-btn-primary" disabled={savingBio} onClick={this.saveBio}>{savingBio ? 'Saving...' : 'Save'}</button>
                        </div>
                        {bioError ? <p className="pf-error" role="alert">{bioError}</p> : null}
                    </div>
                    : profile.bio ?
                        <p className="pf-bio">{profile.bio}</p>
                        :
                        <p className="pf-empty">{profile.isMine ? "You haven't written a bio yet." : `${profile.username} hasn't written a bio yet.`}</p>
                }
            </section>
        );
    }

    renderCards() {
        let { profile } = this.state;
        let cards = profile.cards;
        return (
            <section className="pf-section">
                <div className="pf-section-head">
                    <h2>Trading cards</h2>
                    <div className="pf-section-actions">
                        {profile.isMine ? <Link className="pf-btn pf-btn-ghost" to="/cards">Open packs</Link> : null}
                        <Link className="pf-btn pf-btn-primary" to={`/profile/${encodeURIComponent(profile.username)}/cards`}>View collection</Link>
                    </div>
                </div>
                <div className="pf-stats">
                    <div className="pf-stat"><strong>{cards.unique} / {cards.total}</strong><span>Cards collected</span></div>
                    <div className="pf-stat"><strong>{cards.packsOpened.toLocaleString()}</strong><span>Packs opened</span></div>
                    <div className="pf-stat"><strong>{cards.copies.toLocaleString()}</strong><span>Cards pulled</span></div>
                    <div className="pf-stat pf-stat-trimmed"><strong>{cards.trimmed}</strong><span>Trimmed</span></div>
                    <div className="pf-stat pf-stat-mystic"><strong>{cards.mystic}</strong><span>Mystic</span></div>
                </div>
                <div className="pf-breakdown">
                    <ul className="pf-bars">
                        {cards.sets.map((set) => (
                            <li key={set.id} className={`pf-bar-row pf-set-${set.id}`}>
                                <span className="pf-bar-label">{set.name}</span>
                                <span className="pf-bar" role="progressbar" aria-label={`${set.name} cards collected`} aria-valuemin="0" aria-valuemax={set.total} aria-valuenow={set.unique}>
                                    <span style={{ width: `${set.total ? (set.unique / set.total) * 100 : 0}%` }}></span>
                                </span>
                                <span className="pf-bar-count">{set.unique} / {set.total}</span>
                            </li>
                        ))}
                    </ul>
                    <ul className="pf-bars">
                        {RARITIES.map((rarity) => {
                            let held = cards.byRarity[rarity.id] || { unique: 0, total: 0 };
                            return (
                                <li key={rarity.id} className={`pf-bar-row pf-rarity-${rarity.id}`}>
                                    <span className="pf-bar-label">{rarity.label}</span>
                                    <span className="pf-bar" role="progressbar" aria-label={`${rarity.label} cards collected`} aria-valuemin="0" aria-valuemax={held.total} aria-valuenow={held.unique}>
                                        <span style={{ width: `${held.total ? (held.unique / held.total) * 100 : 0}%` }}></span>
                                    </span>
                                    <span className="pf-bar-count">{held.unique} / {held.total}</span>
                                </li>
                            );
                        })}
                    </ul>
                </div>
                <h3>Rarest cards</h3>
                {cards.rarest.length === 0 ?
                    <p className="pf-empty">{profile.isMine ? "You haven't collected any cards yet." : `${profile.username} hasn't collected any cards yet.`}</p>
                    :
                    <ul className="pf-rarest">
                        {cards.rarest.map((held) => (
                            <li key={held.card.id}>
                                <TradingCard card={held.card} total={held.total} count={held.copies} trimmed={held.trimmed} mystic={held.mystic} />
                            </li>
                        ))}
                    </ul>
                }
            </section>
        );
    }

    // The scores, a game at a time, with a tab for each game
    renderGames() {
        let { scoreGame } = this.state;
        return (
            <section className="pf-section">
                <div className="pf-section-head">
                    <h2>Scores</h2>
                    <Link className="pf-btn pf-btn-ghost" to="/leaderboard">Leaderboard</Link>
                </div>
                <div className={`pf-tabs pf-tabs-${scoreGame}`} role="tablist" aria-label="Game">
                    {SCORE_GAMES.map((game) => (
                        <button key={game.id} type="button" role="tab" aria-selected={game.id === scoreGame}
                            className={game.id === scoreGame ? 'is-active' : ''} onClick={() => this.setState({ scoreGame: game.id })}>
                            {game.name}
                        </button>
                    ))}
                </div>
                {this.renderScores(scoreGame)}
            </section>
        );
    }

    renderScores(game) {
        let { profile } = this.state;
        if (game === 'solarflare') {
            return <p className="pf-empty">Solarflare doesn't report scores to the website yet.</p>;
        }
        if (game === 'clompyclicker') {
            let diamonds = profile.games.clompyclicker;
            if (!diamonds) {
                return <p className="pf-empty">No Clompy Clicker diamonds yet.</p>;
            }
            return (
                <div className="pf-table-wrap">
                    <table className="pf-table">
                        <thead>
                            <tr>
                                <th>Board</th>
                                <th className="pf-num">Score</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>All-time diamonds</td>
                                <td className="pf-num"><Record record={diamonds} format={(n) => n.toLocaleString()} /></td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            );
        }
        let records = profile.games.spacejump;
        return (
            <>
                {records.length === 0 ?
                    <p className="pf-empty">No Space Jump records yet.</p>
                    :
                    <div className="pf-table-wrap">
                        <table className="pf-table">
                            <thead>
                                <tr>
                                    <th>Board</th>
                                    <th className="pf-num">Best time</th>
                                    <th className="pf-num">Fewest jumps</th>
                                </tr>
                            </thead>
                            <tbody>
                                {records.map((record) => (
                                    <tr key={record.board} className={record.board === 'run' ? 'pf-run' : ''}>
                                        <td>{record.name}</td>
                                        <td className="pf-num"><Record record={record.time} format={formatTime} /></td>
                                        <td className="pf-num"><Record record={record.jumps} format={(n) => n.toLocaleString()} /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                }
            </>
        );
    }

    // The achievements, a game at a time: a tab for each game, and under it the ones the player has earned, with a
    // button to see the rest
    renderAchievements() {
        let { profile, allAchievements } = this.state;
        let games = profile.achievements || [];
        if (games.length === 0) {
            return null;
        }
        let earned = games.reduce((sum, each) => sum + each.earned, 0);
        let total = games.reduce((sum, each) => sum + each.total, 0);
        let game = games.find((each) => each.game === this.state.achievementGame) || games[0];
        let showAll = Boolean(allAchievements[game.game]);
        let shown = game.achievements.filter((achievement) => showAll || achievement.earned);
        return (
            <section className="pf-section">
                <div className="pf-section-head">
                    <h2>Achievements</h2>
                    <span className="pf-achievement-count"><strong>{earned}</strong> / {total}</span>
                </div>
                <div className={`pf-tabs pf-tabs-${game.game}`} role="tablist" aria-label="Game">
                    {games.map((each) => (
                        <button key={each.game} type="button" role="tab" aria-selected={each.game === game.game}
                            className={each.game === game.game ? 'is-active' : ''} onClick={() => this.setState({ achievementGame: each.game })}>
                            {each.name} <span className="pf-achievement-tally">{each.earned} / {each.total}</span>
                        </button>
                    ))}
                </div>
                <div className="pf-achievement-head">
                    <button type="button" className="pf-link-btn" aria-expanded={showAll}
                        onClick={() => this.setState({ allAchievements: { ...allAchievements, [game.game]: !showAll } })}>
                        {showAll ? 'Show earned only' : `Show all ${game.total}`}
                    </button>
                </div>
                {shown.length === 0 ?
                    <p className="pf-empty">No {game.name} achievements yet.</p>
                    :
                    <ul className="pf-achievements">
                        {shown.map((achievement) => (
                            <li key={achievement.id} className={achievement.earned ? 'is-earned' : ''}
                                title={achievement.earnedAt ? `Earned ${new Date(achievement.earnedAt).toLocaleDateString()}` : undefined}>
                                <strong>{achievement.title || '???'}</strong>
                                <span>{achievement.description || 'A secret, until it is earned.'}</span>
                                {/* the Clomp Coins it pays */}
                                {achievement.coins ? <Coins amount={achievement.coins} className="pf-achievement-coins" /> : null}
                                <span className="sr-only">{achievement.earned ? ' (earned)' : ' (not earned yet)'}</span>
                            </li>
                        ))}
                    </ul>
                }
            </section>
        );
    }

    renderBody() {
        let { loading, profile, problem } = this.state;
        if (loading) {
            return <p className="pf-status">Loading profile...</p>;
        }
        if (problem === 'login') {
            return (
                <div className="pf-status">
                    <p>Log in to see your profile.</p>
                    <Link className="pf-btn pf-btn-primary" to="/login">Log in</Link>
                </div>
            );
        }
        if (problem === 'missing') {
            return <p className="pf-status">There's no player with that profile.</p>;
        }
        if (problem || !profile) {
            return <p className="pf-status">Couldn't load this profile right now. Please try again later.</p>;
        }
        return (
            <>
                <header className="pf-header">
                    <DefaultAvatar />
                    <div>
                        <p className="pf-eyebrow">{profile.isMine ? 'Your profile' : 'Player profile'}</p>
                        <h1>{profile.username}</h1>
                        {(profile.badges || []).length > 0 ?
                            <ul className="pf-badges" aria-label="Badges">
                                {profile.badges.map((badge) => <li key={badge.id} className={`pf-badge pf-badge-${badge.id}`}>{badge.name}</li>)}
                            </ul>
                            : null}
                        {/* on the site or in a game right now, or when they last were */}
                        <Presence online={profile.online} lastSeen={profile.lastSeen} hidden={profile.presenceHidden} mine={profile.isMine} />
                        {profile.joined ? <p className="pf-joined">Joined {new Date(profile.joined).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}</p> : null}
                    </div>
                    {profile.isMine ?
                        <button type="button" className="pf-settings-btn" aria-label="Account settings" title="Account settings" onClick={() => this.setState({ showSettings: true })}>
                            <SettingsIcon />
                        </button>
                        : null}
                </header>
                {this.renderBio()}
                {this.renderCards()}
                {this.renderGames()}
                {this.renderAchievements()}
                {this.state.showSettings && profile.isMine ?
                    <ProfileSettings onClose={() => this.setState({ showSettings: false })} onSaved={this.settingsSaved} onDeleted={this.accountDeleted} />
                    : null}
            </>
        );
    }

    render() {
        return (
            <>
                <Navbar history={this.props.history}></Navbar>
                <div className="pf-page">
                    {this.renderBody()}
                </div>
            </>
        );
    }

}
